import React, { useState } from 'react';
import { Card, CardBody, Form, FormGroup, Label, Input, Alert, Spinner } from 'reactstrap';
import { customerInfoSubmitRequest } from '../../api/controller/CustomerInfoPublicController';
import logo from '../../assets/images/logo.svg';

const TRAVELER_TYPES = [
    { value: 'TOURIST', label: 'Tourist' },
    { value: 'MALDIVIAN', label: 'Maldivian' },
    { value: 'WORK_VISA', label: 'Work Visa Holder' },
];

const CONTACT_METHODS = [
    { value: 'PHONE', label: 'Via phone' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'WHATSAPP', label: 'WhatsApp' },
    { value: 'TELEGRAM', label: 'Telegram' },
    { value: 'VIBER', label: 'Viber' },
];

const EMPTY = {
    full_name: '', arrival_flight: '', arrival_date: '', traveler_type: '',
    place_of_stay: '', hotel_contact: '', return_flight: '', return_date: '',
    address: '', road: '', zip: '', city: '', country: '',
    email: '', mobile: '', contact_methods: [],
    website: '', // honeypot, must stay empty
};

const sectionStyle = { fontSize: 15, fontWeight: 700, color: '#0f766e', margin: '8px 0 16px' };
const labelStyle = { fontWeight: 600, fontSize: 13, color: '#374151', marginBottom: 6 };
const inputStyle = { borderRadius: 6, fontSize: 14, height: 42 };

const CustomerInformation = () => {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [reference, setReference] = useState(null);

    const isTourist = form.traveler_type === 'TOURIST';
    const clearError = (name) => setErrors(e => (e[name] ? { ...e, [name]: undefined } : e));
    const set = (name, value) => {
        setForm(f => ({ ...f, [name]: value }));
        clearError(name);
    };
    const fieldError = (name) => errors[name]?.[0];

    const toggleMethod = (value) => {
        clearError('contact_methods');
        setForm(f => ({
            ...f,
            contact_methods: f.contact_methods.includes(value)
                ? f.contact_methods.filter(m => m !== value)
                : [...f.contact_methods, value],
        }));
    };

    const validate = () => {
        const e = {};
        const need = (name, label) => { if (!String(form[name]).trim()) e[name] = [`${label} is required.`]; };
        need('full_name', 'Full name');
        need('arrival_flight', 'Arrival flight number');
        need('arrival_date', 'Arrival date');
        need('traveler_type', 'Traveler type');
        if (isTourist) {
            need('place_of_stay', 'Place of stay');
            need('return_flight', 'Return flight number');
            need('return_date', 'Return date');
            if (form.return_date && form.arrival_date && form.return_date < form.arrival_date) {
                e.return_date = ['Return date cannot be before the arrival date.'];
            }
        }
        need('address', 'Residential address');
        need('city', 'District/City/Island');
        need('country', 'Country');
        need('email', 'Email');
        need('mobile', 'Mobile number');
        if (form.contact_methods.length === 0) e.contact_methods = ['Select at least one contact method.'];
        return e;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        setError(null);
        const local = validate();
        setErrors(local);
        if (Object.keys(local).length > 0) return;

        setLoading(true);
        const payload = { ...form };
        if (!isTourist) {
            ['place_of_stay', 'hotel_contact', 'return_flight', 'return_date'].forEach(k => { payload[k] = null; });
        }
        const response = await customerInfoSubmitRequest(payload);
        setLoading(false);

        if (response?.Status === 'success') {
            setReference(response.Reference || null);
            setDone(true);
            window.scrollTo(0, 0);
            return;
        }
        setErrors(response?.errors || {});
        setError(response?.error === 'Too Many Attempts.'
            ? 'Too many submissions. Please wait a minute and try again.'
            : Object.keys(response?.errors || {}).length > 0
                ? 'Please correct the highlighted fields.'
                : 'An error occurred. Please try again.');
    };

    const text = (name, label, { required, hint, type = 'text', placeholder } = {}) => (
        <FormGroup style={{ marginBottom: 18 }}>
            <Label style={labelStyle}>{label}{required && <span style={{ color: '#dc2626' }}> *</span>}</Label>
            <Input
                type={type}
                value={form[name]}
                placeholder={placeholder}
                invalid={!!fieldError(name)}
                onChange={e => set(name, e.target.value)}
                style={inputStyle}
            />
            {hint && <small style={{ color: '#9ca3af', fontSize: 11, display: 'block', marginTop: 4 }}>{hint}</small>}
            {fieldError(name) && <small style={{ color: '#dc2626', fontSize: 12 }}>{fieldError(name)}</small>}
        </FormGroup>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#f0f4f8', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#0f172a', padding: '14px 32px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <img src={logo} alt="Logo" style={{ height: 28, opacity: 0.9 }} />
                <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500, letterSpacing: '0.04em' }}>
                    BAGGAGE SERVICES
                </span>
            </div>

            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '40px 16px 32px' }}>
                <div style={{ width: '100%', maxWidth: 680 }}>
                    <div style={{ textAlign: 'center', marginBottom: 28 }}>
                        <h4 style={{ fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Customer Information</h4>
                        <p style={{ color: '#64748b', fontSize: 14, marginTop: 6, marginBottom: 0 }}>
                            Please provide your details so our Baggage Services team can assist and keep you updated.
                        </p>
                    </div>

                    <Card style={{ border: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)', borderRadius: 10 }}>
                        <CardBody style={{ padding: '28px 32px' }}>
                            {done ? (
                                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                                    <i className="mdi mdi-check-circle-outline" style={{ fontSize: 48, color: '#16a34a' }}></i>
                                    <h5 style={{ marginTop: 12 }}>Thank you</h5>
                                    <p style={{ color: '#64748b', fontSize: 14, marginBottom: 0 }}>
                                        Your information has been submitted. A confirmation has been sent to your email.
                                    </p>
                                    {reference && (
                                        <p style={{ marginTop: 12, marginBottom: 0, fontSize: 14 }}>
                                            Your reference: <strong>{reference}</strong>
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <Form onSubmit={handleSubmit} noValidate>
                                    {/* Honeypot: hidden from people, bots fill it in */}
                                    <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                                        <input type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={e => set('website', e.target.value)} />
                                    </div>

                                    <div style={sectionStyle}>Your name and flight details</div>
                                    {text('full_name', 'Full Name', { required: true, hint: 'If travelling with more than one person, provide the name on the baggage claim stub.' })}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 20px' }}>
                                        {text('arrival_flight', 'Arrival Flight Number', { required: true, placeholder: 'e.g. QR672' })}
                                        {text('arrival_date', 'Arrival Date', { required: true, type: 'date' })}
                                    </div>
                                    <FormGroup style={{ marginBottom: 18 }}>
                                        <Label style={labelStyle}>Traveler Type<span style={{ color: '#dc2626' }}> *</span></Label>
                                        <Input type="select" value={form.traveler_type} invalid={!!fieldError('traveler_type')}
                                            onChange={e => set('traveler_type', e.target.value)} style={inputStyle}>
                                            <option value="">Select your answer</option>
                                            {TRAVELER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                        </Input>
                                        {fieldError('traveler_type') && <small style={{ color: '#dc2626', fontSize: 12 }}>{fieldError('traveler_type')}</small>}
                                    </FormGroup>

                                    {isTourist && (
                                        <>
                                            <div style={sectionStyle}>Your hotel, returning flight and date</div>
                                            {text('place_of_stay', 'Place of Stay in Maldives (Hotel, Guesthouse, Boat etc.)', { required: true })}
                                            {text('hotel_contact', 'Contact Number of your hotel', { hint: 'Only if you know' })}
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 20px' }}>
                                                {text('return_flight', 'Return Flight Number from Maldives', { required: true })}
                                                {text('return_date', 'Return Date from Maldives', { required: true, type: 'date' })}
                                            </div>
                                        </>
                                    )}

                                    <div style={sectionStyle}>Living or home address</div>
                                    {text('address', 'Residential Address', { required: true, hint: 'Company/Agent name for work visa passengers' })}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 20px' }}>
                                        {text('road', 'Road/Street')}
                                        {text('zip', 'Zip/Postal')}
                                        {text('city', 'District/City/Island', { required: true })}
                                        {text('country', 'Country', { required: true })}
                                    </div>

                                    <div style={sectionStyle}>Contact information</div>
                                    {text('email', 'Email', { required: true, type: 'email' })}
                                    {text('mobile', 'Mobile Number (with Country Code)', { required: true, placeholder: '+960 7XXXXXX' })}
                                    <FormGroup style={{ marginBottom: 20 }}>
                                        <Label style={labelStyle}>Preferred Methods of Contact<span style={{ color: '#dc2626' }}> *</span></Label>
                                        <small style={{ color: '#9ca3af', fontSize: 11, display: 'block', marginBottom: 6 }}>
                                            Select the communication channels we can use to reach you
                                        </small>
                                        {CONTACT_METHODS.map(m => (
                                            <FormGroup check key={m.value}>
                                                <Label check style={{ fontSize: 14 }}>
                                                    <Input type="checkbox" checked={form.contact_methods.includes(m.value)} onChange={() => toggleMethod(m.value)} />
                                                    {' '}{m.label}
                                                </Label>
                                            </FormGroup>
                                        ))}
                                        {fieldError('contact_methods') && <small style={{ color: '#dc2626', fontSize: 12 }}>{fieldError('contact_methods')}</small>}
                                    </FormGroup>

                                    {error && (
                                        <Alert color="danger" style={{ borderRadius: 6, fontSize: 13, padding: '10px 14px', marginBottom: 16 }}>
                                            <i className="mdi mdi-alert-circle-outline me-1"></i>{error}
                                        </Alert>
                                    )}

                                    <button type="submit" disabled={loading} style={{
                                        width: '100%', height: 44, borderRadius: 6, border: 'none',
                                        background: loading ? '#93c5fd' : '#0ea5e9', color: '#fff', fontWeight: 600, fontSize: 14,
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                    }}>
                                        {loading ? <><Spinner size="sm" /><span>Submitting...</span></> : <span>Submit</span>}
                                    </button>
                                </Form>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default CustomerInformation;
